import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, Dumbbell, ChevronLeft, ChevronRight } from "lucide-react";

const BODY_PARTS = ["all", "back", "cardio", "chest", "lower arms", "lower legs", "neck", "shoulders", "upper arms", "upper legs", "waist"];

const bodyPartColors = {
  back: "bg-blue-100 text-blue-700",
  cardio: "bg-pink-100 text-pink-700",
  chest: "bg-red-100 text-red-700",
  "lower arms": "bg-orange-100 text-orange-700",
  "lower legs": "bg-lime-100 text-lime-700",
  neck: "bg-gray-100 text-gray-700",
  shoulders: "bg-purple-100 text-purple-700",
  "upper arms": "bg-amber-100 text-amber-700",
  "upper legs": "bg-green-100 text-green-700",
  waist: "bg-teal-100 text-teal-700",
};

export default function ExercisePickerDialogV2({ open, onOpenChange, onSelect }) {
  const [search, setSearch] = useState("");
  const [bodyPart, setBodyPart] = useState("all");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);

  const { data: exercises = [], isLoading } = useQuery({
    queryKey: ["exercises-db"],
    queryFn: () => base44.entities.Exercises.list("name", 1500),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const filtered = exercises.filter(ex => {
    const matchSearch = !search || ex.name?.toLowerCase().includes(search.toLowerCase()) ||
      (ex.targetMuscles || []).some(m => m.toLowerCase().includes(search.toLowerCase())) ||
      (ex.equipments || []).some(e => e.toLowerCase().includes(search.toLowerCase()));
    const matchPart = bodyPart === "all" || (ex.bodyParts || []).some(b => b.toLowerCase() === bodyPart);
    return matchSearch && matchPart;
  });

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const handleFilterChange = (newSearch, newBodyPart) => {
    setPage(1);
    if (newSearch !== undefined) setSearch(newSearch);
    if (newBodyPart !== undefined) setBodyPart(newBodyPart);
  };

  const handleSelect = (ex) => {
    onSelect({
      name: ex.name,
      exerciseId: ex.exerciseId,
      gifUrl: ex.gifUrl,
      bodyParts: ex.bodyParts,
      targetMuscles: ex.targetMuscles,
      equipments: ex.equipments,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] flex flex-col gap-3">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Dumbbell className="w-5 h-5 text-primary" />
            Exercise Library ({exercises.length} exercises)
          </DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search by name, muscle, or equipment..."
            value={search}
            onChange={e => handleFilterChange(e.target.value, undefined)}
          />
        </div>

        <div className="flex gap-1.5 flex-wrap">
          {BODY_PARTS.map(part => (
            <button
              key={part}
              onClick={() => handleFilterChange(undefined, part)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize transition-all border ${
                bodyPart === part
                  ? "bg-primary text-white border-primary"
                  : "bg-muted text-muted-foreground border-transparent hover:border-primary/30"
              }`}
            >
              {part}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto space-y-2 pr-1 min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-8 text-sm">No exercises found</p>
          ) : (
            paginated.map(ex => (
              <div
                key={ex.id}
                className="flex items-start gap-3 p-3 rounded-xl border hover:border-primary/40 hover:bg-muted/30 transition-all group cursor-pointer"
                onClick={() => handleSelect(ex)}
              >
                {ex.gifUrl && (
                  <img
                    src={ex.gifUrl}
                    alt={ex.name}
                    className="w-14 h-14 rounded-lg object-cover shrink-0 bg-muted"
                    loading="lazy"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm capitalize">{ex.name}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(ex.bodyParts || []).map(b => (
                      <Badge key={b} className={`text-[10px] capitalize ${bodyPartColors[b] || "bg-muted text-muted-foreground"}`}>{b}</Badge>
                    ))}
                    {(ex.equipments || []).map(e => (
                      <Badge key={e} variant="outline" className="text-[10px] capitalize">{e}</Badge>
                    ))}
                  </div>
                  {(ex.targetMuscles || []).length > 0 && (
                    <p className="text-xs text-muted-foreground mt-0.5">🎯 {ex.targetMuscles.join(", ")}</p>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity gap-1"
                  onClick={(e) => { e.stopPropagation(); handleSelect(ex); }}
                >
                  <Plus className="w-3.5 h-3.5" /> Add
                </Button>
              </div>
            ))
          )}
        </div>

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Per page:</span>
            <Select value={String(perPage)} onValueChange={v => { setPerPage(Number(v)); setPage(1); }}>
              <SelectTrigger className="h-7 w-16 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">
            {filtered.length === 0 ? "0" : `${(page - 1) * perPage + 1}–${Math.min(page * perPage, filtered.length)}`} of {filtered.length}
          </p>
          <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" className="h-7 w-7" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-xs text-muted-foreground">{page}/{totalPages || 1}</span>
            <Button size="icon" variant="ghost" className="h-7 w-7" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}